import { Radio, ChevronRight } from "lucide-react";
import { C, FONT, card } from "../theme";
import type { CSSProperties } from "react";
import type { DashboardState, ViewKey } from "../types";
import { computeNextRun } from "../lib/nextRun";
import { Kpi } from "../components/Kpi";
import { Hero } from "../components/Hero";
import { BalanceChart } from "../components/BalanceChart";
import { Forecast } from "../components/Forecast";
import { FeedRow } from "../components/FeedRow";
import { ZoneCard } from "../components/ZoneCard";
import { Et0 } from "../components/Et0";

/** Card with a staggered entrance — large 20px radius for the panel surfaces. */
function panelStyle(index: number, radius = 20): CSSProperties {
  return {
    ...card,
    borderRadius: radius,
    padding: 20,
    animationDelay: `${index * 60}ms`,
  };
}

const sectionTitle: CSSProperties = {
  fontFamily: FONT.serif,
  fontSize: 18,
  fontWeight: 500,
  color: C.ink,
  lineHeight: 1.1,
};

const linkButton: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 13,
  fontWeight: 500,
  color: C.sea,
  background: "transparent",
  padding: 0,
};

export function Overview({
  state,
  pending,
  onRun,
  onStop,
  onStep,
  onToggle,
  onSelect,
  onNav,
}: {
  state: DashboardState;
  pending: Record<string, boolean>;
  onRun: (id: string, minutes: number) => void;
  onStop: (id: string) => void;
  onStep: (id: string, minutes: number) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onSelect: (id: string) => void;
  onNav: (v: ViewKey) => void;
}) {
  const { zones } = state;

  // The single canonical "what runs next + why + savings" — shared with the
  // Schedule hero so the two surfaces can never name different next zones.
  const nr = computeNextRun(state);

  const watering = zones.filter((z) => z.state === "watering").length;
  const holding = zones.filter(
    (z) => z.state === "wind-hold" || z.state === "freeze-hold",
  ).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      {/* KPI row — auto-reflows 4 → 2 → 1 without fixed breakpoints */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 18 }}>
        {state.kpis.map((k, i) => (
          <div key={i} className="card-in" style={{ animationDelay: `${i * 60}ms` }}>
            <Kpi kpi={k} />
          </div>
        ))}
      </div>

      {/* Main grid: 3 cols on wide — left col-span-2 stack + right engine feed */}
      <div className="marram-overview-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 22 }}>
        <style>{`@media (min-width: 1040px){.marram-overview-grid{grid-template-columns:2fr 1fr !important}}`}</style>

        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div className="card-in" style={{ animationDelay: "0ms" }}>
            <Hero nr={nr} />
          </div>

          {/* Water balance */}
          <div className="card-in" style={{ ...panelStyle(1), padding: "22px 24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <span style={sectionTitle}>Water balance · 7 days</span>
              <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: C.textMid }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: C.rain }} />
                  Rainfall
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: C.sea }} />
                  Irrigation
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 14, height: 2.5, borderRadius: 2, background: C.beacon }} />
                  <Et0 />&nbsp;demand
                </span>
              </div>
            </div>
            <p style={{ fontSize: 12, color: C.textMid, margin: "-6px 0 16px", maxWidth: "70ch" }}>
              Millimetres of water per day: <Et0 />&nbsp;demand is what sun, heat and wind draw from the
              lawn; rainfall and irrigation are what put it back.
            </p>
            <BalanceChart data={state.balance} />
          </div>

          {/* Forecast */}
          <div className="card-in" style={{ ...panelStyle(2), padding: "22px 24px" }}>
            <div style={{ ...sectionTitle, marginBottom: 16 }}>Forecast · next 24h</div>
            <Forecast cells={state.forecast} />
          </div>
        </div>

        {/* Right column — engine decisions */}
        <div className="card-in" style={{ ...panelStyle(1, 18), alignSelf: "start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 0 }}>
            <Radio size={16} style={{ color: C.sage }} />
            <span style={sectionTitle}>Engine decisions</span>
          </div>
          <p style={{ fontSize: 12, color: C.muted, margin: "5px 0 8px" }}>
            Why Marram ran or held each zone.
          </p>
          <div>
            {state.feed.slice(0, 7).map((e) => (
              <FeedRow key={e.id} e={e} />
            ))}
          </div>
          <button onClick={() => onNav("activity")} style={{ ...linkButton, marginTop: 14 }}>
            See all activity <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Zones section */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <span style={sectionTitle}>
            Zones{" "}
            <span style={{ fontFamily: FONT.sans, fontSize: 14, fontWeight: 400, color: C.muted }}>
              · {watering} watering · {holding} holding
            </span>
          </span>
          <button onClick={() => onNav("zones")} style={linkButton}>
            Manage zones <ChevronRight size={14} />
          </button>
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 270px), 1fr))", gap: 18 }}
        >
          {zones.map((z) => (
            <ZoneCard
              key={z.id}
              z={z}
              paused={state.paused}
              soilSrc={state.sources?.soil}
              flowSrc={state.sources?.flow}
              busyRun={!!pending[`run:${z.id}`]}
              busyStop={!!pending[`stop:${z.id}`]}
              onRun={onRun}
              onStop={onStop}
              onStep={onStep}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
